/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

// API functions for the Bots feature will be implemented here.
import api from './api'; // Assuming 'api' is the configured Axios instance exported from api.js
import API_BASE_URL from "./config"; // For consistency if any non-standard routes are needed.

// --- Bots Management ---

/**
 * List all bots.
 * @returns {Promise<Array>} A promise that resolves to an array of bot objects.
 */
export const listBots = async (options = {}) => {
  try {
    const response = await api.get("/bots/bots/", options);
    return response;
  } catch (error) {
    console.error("API Error in listBots:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch bots";
  }
};

/**
 * Create a new bot.
 * @param {Object} botData - The data for the new bot (e.g., name, account_id, strategy_template).
 * @returns {Promise<Object>} A promise that resolves to the created bot object.
 */
export const createBot = async (botData) => {
  try {
    const response = await api.post("/bots/bots/", botData);
    return response;
  } catch (error) {
    console.error("API Error in createBot:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to create bot";
  }
};

/**
 * Retrieve a specific bot by its ID.
 * @param {string} botId - The ID of the bot to retrieve.
 * @returns {Promise<Object>} A promise that resolves to the bot object.
 */
export const getBot = async (botId) => {
  try {
    const response = await api.get(`/bots/bots/${botId}/`);
    return response;
  } catch (error) {
    console.error(`API Error in getBot (ID: ${botId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch bot ${botId}`;
  }
};

/**
 * Update a specific bot by its ID.
 * @param {string} botId - The ID of the bot to update.
 * @param {Object} botData - The updated data for the bot.
 * @returns {Promise<Object>} A promise that resolves to the updated bot object.
 */
export const updateBot = async (botId, botData) => {
  try {
    const response = await api.put(`/bots/bots/${botId}/`, botData);
    return response;
  } catch (error) {
    console.error(`API Error in updateBot (ID: ${botId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to update bot ${botId}`;
  }
};

/**
 * Partially update a specific bot by its ID.
 * @param {string} botId - The ID of the bot to update.
 * @param {Object} botData - The partial data for the bot.
 * @returns {Promise<Object>} A promise that resolves to the updated bot object.
 */
export const partialUpdateBot = async (botId, botData) => {
  try {
    const response = await api.patch(`/bots/bots/${botId}/`, botData);
    return response;
  } catch (error) {
    console.error(`API Error in partialUpdateBot (ID: ${botId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to partially update bot ${botId}`;
  }
};

/**
 * Delete a specific bot by its ID.
 * @param {string} botId - The ID of the bot to delete.
 * @returns {Promise<void>} A promise that resolves when the bot is deleted.
 */
export const deleteBot = async (botId) => {
  try {
    await api.delete(`/bots/bots/${botId}/`);
  } catch (error) {
    console.error(`API Error in deleteBot (ID: ${botId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to delete bot ${botId}`;
  }
};

// --- Bot Versions Management ---

/**
 * List all bot versions. Can be filtered by bot_id.
 * @param {string} [botId] - Optional ID of the bot to filter versions for.
 * @returns {Promise<Array>} A promise that resolves to an array of bot version objects.
 */
export const listBotVersions = async (botId) => {
  try {
    const params = botId ? { bot_id: botId } : {};
    const response = await api.get("/bots/versions/", { params });
    return response;
  } catch (error) {
    const botIdMsg = botId ? ` for bot ${botId}` : "";
    console.error(`API Error in listBotVersions${botIdMsg}:`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch bot versions${botIdMsg}`;
  }
};

/**
 * Create a new bot version.
 * @param {string} botId - The ID of the bot for which to create the version.
 * @param {Object} versionData - Data for the new version ({ strategy_name, strategy_params, indicator_configs, notes }).
 * @returns {Promise<Object>} A promise that resolves to the created bot version object.
 */
export const createBotVersion = async (botId, versionData) => {
  try {
    const payload = { ...versionData, bot_id: botId };
    const response = await api.post("/bots/versions/", payload);
    return response;
  } catch (error) {
    console.error("API Error in createBotVersion:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to create bot version";
  }
};

/**
 * Retrieve a specific bot version by its ID.
 * @param {string} versionId - The ID of the bot version to retrieve.
 * @returns {Promise<Object>} A promise that resolves to the bot version object.
 */
export const getBotVersion = async (versionId) => {
  try {
    const response = await api.get(`/bots/versions/${versionId}/`);
    return response;
  } catch (error) {
    console.error(`API Error in getBotVersion (ID: ${versionId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch bot version ${versionId}`;
  }
};

/**
 * Update a specific bot version by its ID.
 * (Note: Typically, versions are immutable. Updates might be limited).
 * @param {string} versionId - The ID of the bot version to update.
 * @param {Object} versionData - The updated data for the bot version.
 * @returns {Promise<Object>} A promise that resolves to the updated bot version object.
 */
export const updateBotVersion = async (versionId, versionData) => {
  try {
    const response = await api.put(`/bots/versions/${versionId}/`, versionData);
    return response;
  } catch (error) {
    console.error(`API Error in updateBotVersion (ID: ${versionId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to update bot version ${versionId}`;
  }
};

/**
 * Partially update a specific bot version by its ID.
 * @param {string} versionId - The ID of the bot version to update.
 * @param {Object} versionData - The partial data for the bot version.
 * @returns {Promise<Object>} A promise that resolves to the updated bot version object.
 */
export const partialUpdateBotVersion = async (versionId, versionData) => {
  try {
    const response = await api.patch(`/bots/versions/${versionId}/`, versionData);
    return response;
  } catch (error) {
    console.error(`API Error in partialUpdateBotVersion (ID: ${versionId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to partially update bot version ${versionId}`;
  }
};

/**
 * Updates the friendly name of a bot version.
 * @param {string} versionId - The ID of the bot version to update.
 * @param {string} versionName - The new friendly name.
 * @returns {Promise<Object>} A promise that resolves to the updated bot version object.
 */
export const updateBotVersionName = async (versionId, versionName) => {
  try {
    const response = await partialUpdateBotVersion(versionId, { version_name: versionName });
    return response;
  } catch (error) {
    console.error(`API Error in updateBotVersionName (ID: ${versionId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to update bot version name for ${versionId}`;
  }
};

/**
 * Delete a specific bot version by its ID.
 * @param {string} versionId - The ID of the bot version to delete.
 * @returns {Promise<void>} A promise that resolves when the bot version is deleted.
 */
export const deleteBotVersion = async (versionId) => {
  try {
    await api.delete(`/bots/versions/${versionId}/`);
  } catch (error) {
    console.error(`API Error in deleteBotVersion (ID: ${versionId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to delete bot version ${versionId}`;
  }
};

// --- New API Endpoints for Modular Bots ---

/**
 * Fetch metadata for all available strategies.
 * @returns {Promise<Array>} A promise that resolves to an array of strategy metadata objects.
 */
export const fetchStrategiesMetadata = async () => {
  try {
    const response = await api.get("/bots/strategies/metadata/");
    return response;
  } catch (error) {
    console.error("API Error in fetchStrategiesMetadata:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch strategies metadata";
  }
};

/**
 * Fetch metadata for all available indicators.
 * @returns {Promise<Array>} A promise that resolves to an array of indicator metadata objects.
 */

// --- Backtest Configuration Management ---

/**
 * List all backtest configurations. Can be filtered by bot_version_id and scope.
 * Backward compatible: pass a string botVersionId, or an object { bot_version_id, scope }.
 * @param {string|{ bot_version_id?: string, scope?: 'version'|'bot'|'user' }} [arg]
 * @returns {Promise<Array>} A promise that resolves to an array of backtest config objects.
 */
export const listBacktestConfigs = async (arg) => {
  try {
    let params = {};
    if (typeof arg === 'string') {
      params = arg ? { bot_version_id: arg } : {};
    } else if (arg && typeof arg === 'object') {
      const { bot_version_id, scope } = arg;
      if (bot_version_id) params.bot_version_id = bot_version_id;
      if (scope) params.scope = scope;
    }
    // Try to fetch all configs, handling common DRF pagination styles.
    // Prefer a large page size to reduce requests.
    const PAGE_SIZE = 500;

    // First request
    const firstResp = await api.get("/bots/backtest-configs/", { params: { ...params, page_size: PAGE_SIZE, limit: PAGE_SIZE } });
    const firstData = firstResp?.data ?? firstResp;

    // If backend returns a plain array, we're done.
    if (Array.isArray(firstData)) return firstData;

    // If backend returns a paginated object { results, count, next, previous }
    const results = [];
    const firstBatch = Array.isArray(firstData?.results) ? firstData.results : [];
    results.push(...firstBatch);

    // Follow 'next' links if provided (DRF pagination)
    let nextUrl = firstData?.next || null;
    let guard = 0;
    while (nextUrl && guard < 20) {
      try {
        const resp = await api.get(nextUrl);
        const data = resp?.data ?? resp;
        const batch = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        if (!batch.length) break;
        results.push(...batch);
        nextUrl = data?.next || null;
      } catch {
        break;
      }
      guard++;
    }

    // If no 'results' key and not an array, attempt limit/offset pagination as a fallback
    if (!firstData?.results && !Array.isArray(firstData)) {
      let offset = PAGE_SIZE;
      for (let i = 0; i < 20; i++) {
        try {
          const resp = await api.get("/bots/backtest-configs/", { params: { ...params, limit: PAGE_SIZE, offset } });
          const data = resp?.data ?? resp;
          const batch = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          if (!batch.length) break;
          results.push(...batch);
          if (!data?.next && batch.length < PAGE_SIZE) break;
          offset += PAGE_SIZE;
        } catch {
          break;
        }
      }
    }

    // If we didn't collect anything but firstData exists in a non-array form, try to return best effort
    return results.length ? results : (firstBatch.length ? firstBatch : []);
  } catch (error) {
    const versionIdMsg = typeof arg === 'string' && arg ? ` for version ${arg}` : '';
    console.error(`API Error in listBacktestConfigs${versionIdMsg}:`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch backtest configs${versionIdMsg}`;
  }
};

/**
 * Create a new backtest configuration.
 * @param {Object} configData - Data for the new config (bot_version | bot_id omitted for user scope, timeframe, risk_json, execution_config_id?, slippage_ms?, slippage_r?, label?).
 * @returns {Promise<Object>} A promise that resolves to the created backtest config object.
 */
// V2: timeframe removed from config. If caller supplies `spec` we POST to new path.
// Backward compat: if legacy shape (sectioned_spec / timeframe) use old endpoint.
export const createBacktestConfig = async (configData) => {
  try {
    const isV2 = !!configData?.spec; // new contract example: { name, spec:{...}, default_timeframe? }
    const path = isV2 ? "/backtests/configs" : "/bots/backtest-configs/";
    const response = await api.post(path, configData);
    return response?.data ? response : response; // keep shape
  } catch (error) {
    console.error("API Error in createBacktestConfig:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to create backtest configuration";
  }
};

/**
 * Retrieve a specific backtest configuration by its ID.
 * @param {string} configId - The ID of the backtest config to retrieve.
 * @returns {Promise<Object>} A promise that resolves to the backtest config object.
 */
export const getBacktestConfig = async (configId) => {
  // Try new path first, fall back to legacy for backward compatibility
  const tryPaths = [
    `/backtests/configs/${configId}`,
    `/bots/backtest-configs/${configId}/`,
  ];
  let lastErr;
  for (const p of tryPaths) {
    try {
      const resp = await api.get(p);
      return resp?.data ?? resp;
    } catch (e) {
      lastErr = e;
    }
  }
  console.error(`API Error in getBacktestConfig (ID: ${configId}):`, lastErr?.response?.data || lastErr?.message);
  throw lastErr?.response?.data || lastErr?.message || `Failed to fetch backtest configuration ${configId}`;
};

/**
 * Update a specific backtest configuration by its ID.
 * @param {string} configId - The ID of the backtest config to update.
 * @param {Object} configData - The updated data for the backtest config.
 * @returns {Promise<Object>} A promise that resolves to the updated backtest config object.
 */
export const updateBacktestConfig = async (configId, configData) => {
  try {
    const path = configData?.spec ? `/backtests/configs/${configId}` : `/bots/backtest-configs/${configId}/`;
    const response = await api.put(path, configData);
    return response?.data ? response : response;
  } catch (error) {
    console.error(`API Error in updateBacktestConfig (ID: ${configId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to update backtest configuration ${configId}`;
  }
};

/**
 * Partially update a specific backtest configuration by its ID.
 * @param {string} configId - The ID of the backtest config to update.
 * @param {Object} configData - The partial data for the backtest config.
 * @returns {Promise<Object>} A promise that resolves to the updated backtest config object.
 */
export const partialUpdateBacktestConfig = async (configId, configData) => {
  try {
    const path = configData?.spec ? `/backtests/configs/${configId}` : `/bots/backtest-configs/${configId}/`;
    const response = await api.patch(path, configData);
    return response?.data ? response : response;
  } catch (error) {
    console.error(`API Error in partialUpdateBacktestConfig (ID: ${configId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to partially update backtest configuration ${configId}`;
  }
};

/**
 * Delete a specific backtest configuration by its ID.
 * @param {string} configId - The ID of the backtest config to delete.
 * @returns {Promise<void>} A promise that resolves when the backtest config is deleted.
 */
export const deleteBacktestConfig = async (configId) => {
  try {
    try { await api.delete(`/backtests/configs/${configId}`); return; } catch {
      // Fall back to the legacy bots-scoped endpoint below.
    }
    await api.delete(`/bots/backtest-configs/${configId}/`);
  } catch (error) {
    console.error(`API Error in deleteBacktestConfig (ID: ${configId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to delete backtest configuration ${configId}`;
  }
};

// --- Backtest Run Management ---

/**
 * List all backtest runs. Can be filtered by config_id or bot_version_id.
 * @param {Object} filters - Optional filters { config_id, bot_version_id }.
 * @returns {Promise<Array>} A promise that resolves to an array of backtest run objects.
 */
export const listBacktestRuns = async (filters = {}) => {
  try {
    const response = await api.get("/bots/backtest-runs/", { params: filters });
    if (Array.isArray(response?.results)) {
      return response.results;
    }
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("API Error in listBacktestRuns:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch backtest runs";
  }
};

/**
 * Retrieve a specific backtest run by its ID.
 * @param {string} runId - The ID of the backtest run to retrieve.
 * @returns {Promise<Object>} A promise that resolves to the backtest run object.
 */
export const getBacktestRun = async (runId) => {
  try {
    const response = await api.get(`/bots/backtest-runs/${runId}/`);
    return response;
  } catch (error) {
    console.error(`API Error in getBacktestRun (ID: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch backtest run ${runId}`;
  }
};

/**
 * Fetch detailed chart data for a specific backtest run.
 * @param {string} runId - The ID of the backtest run.
 * @returns {Promise<Object>} A promise that resolves to the chart data.
 */
export const getBacktestRunChartData = async (runId, params = {}) => {
  try {
  const response = await api.get(`/bots/backtest-runs/${runId}/chart-data/`, { params });
    return response;
  } catch (error) {
    console.error(`API Error in getBacktestRunChartData (ID: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch chart data for backtest run ${runId}`;
  }
};

/**
 * Delete a specific backtest run by its ID.
 * @param {string} runId - The ID of the backtest run to delete.
 * @returns {Promise<void>} A promise that resolves when the backtest run is deleted.
 */
export const deleteBacktestRun = async (runId) => {
  try {
    await api.delete(`/bots/backtest-runs/${runId}/`);
  } catch (error) {
    console.error(`API Error in deleteBacktestRun (ID: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to delete backtest run ${runId}`;
  }
};

/**
 * Re-run a completed backtest run.
 * Backend: POST /bots/backtest-runs/{id}/rerun/
 * @param {string} runId - The ID of the backtest run to re-run.
 * @param {{ preserve_random_seed?: boolean }} [options]
 * @returns {Promise<Object>} A promise that resolves to the newly created backtest run (202 Accepted).
 */
export const rerunBacktestRun = async (runId, options = {}) => {
  try {
    const body = {
      preserve_random_seed: options.preserve_random_seed !== undefined ? options.preserve_random_seed : true,
    };
   const response = await api.post(`/bots/backtest-runs/${runId}/rerun/`, body);
    return response;
  } catch (error) {
    console.error(`API Error in rerunBacktestRun (ID: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to re-run backtest ${runId}`;
  }
};

// --- Live Run Management ---

/**
 * List all live runs. Can be filtered by bot_version_id.
 * @param {Object} filters - Optional filters { bot_version_id }.
 * @returns {Promise<Array>} A promise that resolves to an array of live run objects.
 */
export const listLiveRuns = async (filters = {}) => {
  try {
    const response = await api.get("/bots/live-runs/", { params: filters });
    return response;
  } catch (error) {
    console.error("API Error in listLiveRuns:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch live runs";
  }
};

/**
 * Retrieve a specific live run by its ID.
 * @param {string} runId - The ID of the live run to retrieve.
 * @returns {Promise<Object>} A promise that resolves to the live run object.
 */
export const getLiveRun = async (runId) => {
  try {
    const response = await api.get(`/bots/live-runs/${runId}/`);
    return response;
  } catch (error) {
    console.error(`API Error in getLiveRun (ID: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch live run ${runId}`;
  }
};

// --- Strategy Templates ---

/**
 * List all available strategy template files.
 * @returns {Promise<Array>} A promise that resolves to an array of strategy template objects.
 *                          Each object has "filename" and "display_name".
 */
export const listStrategyTemplates = async () => {
  try {
    const response = await api.get("/bots/strategy-templates/");
    return response;
  } catch (error) {
    console.error("API Error in listStrategyTemplates:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch strategy templates";
  }
};

/**
 * Get parameter definitions for a specific strategy template.
 * @param {string} templateFilename - The filename of the strategy template.
 * @returns {Promise<Array>} A promise that resolves to an array of parameter definition objects.
 */
export const getStrategyTemplateParameters = async (templateFilename) => {
  try {
    const response = await api.get(`/bots/strategy-templates/${templateFilename}/parameters/`);
    return response;
  } catch (error) {
    console.error(`API Error in getStrategyTemplateParameters for ${templateFilename}:`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch parameters for template ${templateFilename}`;
  }
};

// --- Action Endpoints ---

/**
 * Launch a new backtest.
 * @param {Object} launchData - Data for launching the backtest (bot_version_id, backtest_config_id, data_window_start, data_window_end).
 * @returns {Promise<Object>} A promise that resolves to the initiated backtest run object.
 */
// Legacy single launch (config already had timeframe)
export const launchBacktest = async (launchData) => {
  try {
    const response = await api.post("/bots/backtests/launch/", launchData);
    return response;
  } catch (error) {
    console.error("API Error in launchBacktest (legacy):", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to launch backtest (legacy)";
  }
};

// New decoupled timeframe run creation: POST /backtests/runs { config_id, timeframe, data_window:{from,to}, ... }
export const launchBacktestRun = async (data) => {
  try {
    const response = await api.post('/backtests/runs', data);
    return response?.data ?? response;
  } catch (error) {
    // Fallback: if 404, attempt legacy launchBacktest style (only if compatible fields)
    if (error.status === 404) {
      // Only attempt fallback if we have minimum required legacy fields
      if (data.bot_version_id && (data.instrument_symbol || data.instrumentSymbol)) {
        try {
          const legacyPayload = {
            bot_version_id: data.bot_version_id,
            config_id: data.config_id,
            instrument_symbol: data.instrument_symbol || data.instrumentSymbol,
            data_window_start: data?.data_window?.from,
            data_window_end: data?.data_window?.to,
            timeframe: data.timeframe,
            description: data.description,
            include_spread: data.include_spread,
            include_commission: data.include_commission,
            spread_pips: data.spread_pips,
            commission_units: data.commission_units,
            commission_per_unit: data.commission_per_unit,
          };
          const legacy = await launchBacktest(legacyPayload);
          return legacy?.data ?? legacy;
        } catch (legacyErr) {
          console.error('Fallback legacy launch failed:', legacyErr);
        }
      } else {
        console.warn('Skipping fallback legacy launch: missing bot_version_id or instrument_symbol.');
      }
    }
    console.error('API Error in launchBacktestRun:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to launch backtest run';
  }
};

export const launchBacktestRunBatch = async (data) => {
  try {
    const response = await api.post('/backtests/runs/batch', data);
    return response?.data ?? response;
  } catch (error) {
    if (error.status === 404) {
      // Provide a clear hint when batch endpoint not deployed
      throw new Error('Batch launch endpoint not available on this backend (404). Deploy new /backtests/runs/batch or use single run launcher.');
    }
    console.error('API Error in launchBacktestRunBatch:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to launch backtest run batch';
  }
};

/**
 * Start a new live run.
 * @param {Object} startData - Data for starting the live run (bot_version_id).
 * @returns {Promise<Object>} A promise that resolves to the initiated live run object.
 */
export const startLiveRun = async (startData) => {
  try {
    const response = await api.post("/bots/live-runs/start/", startData);
    return response;
  } catch (error) {
    console.error("API Error in startLiveRun:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to start live run";
  }
};

/**
 * Stop an active live run.
 * @param {string} liveRunId - The ID of the live run to stop.
 * @returns {Promise<Object>} A promise that resolves to the live run object with updated status.
 */
export const stopLiveRun = async (liveRunId) => {
  try {
    const response = await api.post(`/bots/live-runs/${liveRunId}/stop/`);
    return response;
  } catch (error) {
    console.error(`API Error in stopLiveRun (ID: ${liveRunId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to stop live run ${liveRunId}`;
  }
};

// --- No-Code Strategy Builder ---

/**
 * Fetch metadata for all available nodes for the strategy builder.
 * @returns {Promise<Object>} A promise that resolves to an object containing indicators, operators, and actions.
 */
export const getBuilderNodesMetadata = async (options = {}) => {
  try {
    const response = await api.get("/bots/nodes/metadata/", options);
    return response;
  } catch (error) {
    console.error("API Error in getBuilderNodesMetadata:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch builder nodes metadata";
  }
};

/**
 * Fetch the unified JSON Schema describing builder strategy/risk including tp_targets ladder.
 * Endpoint returns raw JSON Schema root (not wrapped) – draft 2020-12.
 * @returns {Promise<Object>} JSON Schema object.
 */
export const getBuilderSchema = async (options = {}) => {
  try {
    const response = await api.get("/bots/nodes/schema/", options);
    return response;
  } catch (error) {
    console.error("API Error in getBuilderSchema:", error.response?.data || error.message);
    throw error.response?.data || error.message || "Failed to fetch builder schema";
  }
};

// --- Sectioned Strategy Draft Persistence ---

const BUILDER_DRAFTS_PATH = '/bots/builder/drafts/';

export const listStrategyDrafts = async (botId, options = {}) => {
  try {
    const params = { ...(options.params || {}) };
    if (botId) params.bot_id = botId;
    return await api.get(BUILDER_DRAFTS_PATH, { ...options, params });
  } catch (error) {
    console.error('API Error in listStrategyDrafts:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to list strategy drafts';
  }
};

export const createStrategyDraft = async (payload) => {
  try {
    return await api.post(BUILDER_DRAFTS_PATH, payload);
  } catch (error) {
    console.error('API Error in createStrategyDraft:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to create strategy draft';
  }
};

export const getStrategyDraft = async (draftId) => {
  try {
    return await api.get(`${BUILDER_DRAFTS_PATH}${draftId}/`);
  } catch (error) {
    console.error(`API Error in getStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch strategy draft ${draftId}`;
  }
};

export const updateStrategyDraft = async (draftId, payload) => {
  try {
    return await api.put(`${BUILDER_DRAFTS_PATH}${draftId}/`, payload);
  } catch (error) {
    console.error(`API Error in updateStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to update strategy draft ${draftId}`;
  }
};

export const patchStrategyDraft = async (draftId, payload) => {
  try {
    return await api.patch(`${BUILDER_DRAFTS_PATH}${draftId}/`, payload);
  } catch (error) {
    console.error(`API Error in patchStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to patch strategy draft ${draftId}`;
  }
};

export const deleteStrategyDraft = async (draftId) => {
  try {
    return await api.delete(`${BUILDER_DRAFTS_PATH}${draftId}/`);
  } catch (error) {
    console.error(`API Error in deleteStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to delete strategy draft ${draftId}`;
  }
};

export const normalizeStrategyInput = async (input, sourceFormat = 'auto') => {
  try {
    return await api.post('/bots/builder/normalize/', { input, source_format: sourceFormat });
  } catch (error) {
    console.error('API Error in normalizeStrategyInput:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to normalize strategy input';
  }
};

export const validateStrategyInput = async (input, sourceFormat = 'auto') => {
  try {
    return await api.post('/bots/builder/validate/', { input, source_format: sourceFormat });
  } catch (error) {
    console.error('API Error in validateStrategyInput:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to validate strategy input';
  }
};

export const normalizeStrategyDraft = async (draftId) => {
  try {
    return await api.post(`${BUILDER_DRAFTS_PATH}${draftId}/normalize/`, {});
  } catch (error) {
    console.error(`API Error in normalizeStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to normalize strategy draft ${draftId}`;
  }
};

export const validateStrategyDraft = async (draftId) => {
  try {
    return await api.post(`${BUILDER_DRAFTS_PATH}${draftId}/validate/`, {});
  } catch (error) {
    console.error(`API Error in validateStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to validate strategy draft ${draftId}`;
  }
};

export const publishStrategyDraft = async (draftId, payload = {}) => {
  try {
    return await api.post(`${BUILDER_DRAFTS_PATH}${draftId}/publish/`, payload);
  } catch (error) {
    console.error(`API Error in publishStrategyDraft (${draftId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to publish strategy draft ${draftId}`;
  }
};

export const importBotVersionAsDraft = async (payload) => {
  try {
    return await api.post('/bots/builder/import-version/', payload);
  } catch (error) {
    console.error('API Error in importBotVersionAsDraft:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to import bot version as draft';
  }
};

/**
 * Create a new bot version from a strategy graph.
 * @param {string} botId - The ID of the bot.
 * @param {Object} strategyGraph - The strategy graph object from React Flow.
 * @param {string} [notes] - Optional notes for the new version.
 * @returns {Promise<Object>} A promise that resolves to the created bot version object.
 */

/**
 * Fetches the strategy graph for a specific bot version.
 * @param {string} versionId - The ID of the bot version.
 * @returns {Promise<Object>} A promise that resolves to the strategy graph object.
 */
export const getBotVersionStrategyGraph = async (versionId) => {
  try {
    const response = await api.get(`/bots/versions/${versionId}/strategy-graph/`);
    return response;
  } catch (error) {
    console.error(`API Error in getBotVersionStrategyGraph (ID: ${versionId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to fetch strategy graph for version ${versionId}`;
  }
};

/**
 * Add indicators to a completed backtest run and fetch their computed series.
 * @param {string} runId - The backtest run ID.
 * @param {Array<{name: string, params: object}>} indicators - Indicators to compute.
 * @returns {Promise<Object>} Response containing indicator_data mapping.
 */
export const addBacktestIndicators = async (runId, bodyOrIndicators, params = {}) => {
  try {
    let body;
    if (Array.isArray(bodyOrIndicators)) {
      // Backward compatible: list of { name, params }
      body = { indicators: bodyOrIndicators };
    } else if (bodyOrIndicators && typeof bodyOrIndicators === 'object') {
      // New: pass full body { indicators?, specs? }
      body = bodyOrIndicators;
    } else {
      body = { indicators: [] };
    }
    const response = await api.post(`/bots/backtest-runs/${runId}/indicators/`, body, { params });
    return response;
  } catch (error) {
    console.error(`API Error in addBacktestIndicators (run: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to add indicators for backtest run ${runId}`;
  }
};

/**
 * Filter trades for a backtest run using user-defined conditions.
 * @param {string} runId
 * @param {{ timeframe?: string, apply_to?: 'entry'|'exit', logic?: 'AND'|'OR', conditions: Array<any> }} payload
 * @returns {Promise<{ items: Array<any>, count: number }>}
 */
export const filterBacktestTrades = async (runId, payload) => {
  try {
    const response = await api.post(`/bots/backtest-runs/${runId}/filter-trades/`, payload);
    return response?.data ?? response;
  } catch (error) {
    console.error(`API Error in filterBacktestTrades (run: ${runId}):`, error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to filter trades for backtest run ${runId}`;
  }
};

// --- Filter Presets ---
/**
 * List saved filter presets for the current user, optionally scoped.
 * @param {{ bot_id?: string, bot_version_id?: string }} [params]
 */
export const listFilterPresets = async (params = {}) => {
  try {
    return await api.get('/bots/filters/presets/', { params });
  } catch (error) {
    console.error('API Error in listFilterPresets:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to list filter presets';
  }
};

/**
 * Create a new filter preset.
 * @param {{ name: string, bot?: string, bot_version?: string, payload: any }} body
 */
export const createFilterPreset = async (body) => {
  try {
    return await api.post('/bots/filters/presets/', body);
  } catch (error) {
    console.error('API Error in createFilterPreset:', error.response?.data || error.message);
    throw error.response?.data || error.message || 'Failed to create filter preset';
  }
};

/**
 * Update a filter preset by id.
 * @param {string} presetId
 * @param {any} body
 */
export const updateFilterPreset = async (presetId, body) => {
  try {
    return await api.patch(`/bots/filters/presets/${presetId}/`, body);
  } catch (error) {
    console.error('API Error in updateFilterPreset:', error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to update filter preset ${presetId}`;
  }
};

/**
 * Delete a filter preset by id.
 * @param {string} presetId
 */
export const deleteFilterPreset = async (presetId) => {
  try {
    return await api.delete(`/bots/filters/presets/${presetId}/`);
  } catch (error) {
    console.error('API Error in deleteFilterPreset:', error.response?.data || error.message);
    throw error.response?.data || error.message || `Failed to delete filter preset ${presetId}`;
  }
};

;

// --- Trace & Explain ---

/**
 * Fetch decision trace items for a backtest run.
 * @param {string} runId
 * @param {{ bar_index?: number, ts?: string|number, section?: 'entry'|'exit'|'filter'|'risk'|'fill'|'engine', kind?: string, limit?: number, offset?: number }} [params]
 * @param {{ signal?: AbortSignal, authScheme?: 'Token'|'Bearer' }} [opts]
 * @returns {Promise<{ items: Array<any>, count: number }>} Trace list response
 */
export const getBacktestTrace = async (runId, params = {}, opts = {}) => {
  const {
    bar_index,
    ts,
    section,
    kind,
    limit = 200,
    offset = 0,
  } = params || {};

  const query = {};
  if (bar_index !== undefined && bar_index !== null) query.bar_index = bar_index;
  if (ts !== undefined && ts !== null) query.ts = ts;
  if (section) query.section = section;
  if (kind) query.kind = kind;
  query.limit = limit;
  query.offset = offset;

  const config = { params: query };
  if (opts.signal) config.signal = opts.signal;
  if (opts.authScheme) {
    const token = localStorage.getItem('token');
    if (!config.headers) config.headers = {};
    config.headers['Authorization'] = `${opts.authScheme} ${token}`;
  }

  return api.get(`/bots/backtest-runs/${runId}/trace`, config);
};

/**
 * Explain the decision at a bar for a backtest run.
 * @param {string} runId
 * @param {{ bar_index?: number, ts?: string|number, include?: string }} [params]
 * @param {{ signal?: AbortSignal, authScheme?: 'Token'|'Bearer' }} [opts]
 * @returns {Promise<any>} Explain response
 */
export const getBacktestExplain = async (runId, params = {}, opts = {}) => {
  const { bar_index, ts, include } = params || {};
  if (bar_index === undefined && ts === undefined) {
    throw new Error('bar_index or ts is required');
  }

  const query = {};
  if (bar_index !== undefined && bar_index !== null) query.bar_index = bar_index;
  if (ts !== undefined && ts !== null) query.ts = ts;
  if (include) query.include = include;

  const config = { params: query };
  if (opts.signal) config.signal = opts.signal;
  if (opts.authScheme) {
    const token = localStorage.getItem('token');
    if (!config.headers) config.headers = {};
    config.headers['Authorization'] = `${opts.authScheme} ${token}`;
  }

  return api.get(`/bots/backtest-runs/${runId}/explain`, config);
};
